import { useCallback } from "react";
import {
  saveScheduledMessage,
  deleteScheduledMessage,
  getScheduledMessagesByBatchId,
} from "../db/fsDB";
import { loadGroupKey } from "../utils/Cryptoutils";
import { fetchAndCachePublicKey } from "../utils/pbKey";
import { encryptDualDm, encryptGroup } from "../utils/encrypt";

export function useScheduleAction({
  socket,
  myChatId,
  selectedItem,
  setMessages,
  getUserKey,
  getGroupKey,
  allMessages,
  // ← needed to update the sidebar chat-list preview on edit/delete
  setUsers,
  setGroups,
}) {

  const getChatKey = () => {
    return selectedItem?.type === "group"
      ? getGroupKey(selectedItem.groupId)
      : getUserKey(selectedItem.chatId);
  };

  /* =========================================================
     🔍 BATCH HELPERS
     Priority: IDB (source of truth) → fallback to in-memory allMessages
  ========================================================= */

  /**
   * Resolve siblings for a batch operation.
   *
   * 1. If batchId is provided → query IDB for all records with that batchId.
   *    Each IDB record carries { msgId, chatKey } so we know exactly which
   *    chat every sibling belongs to.
   * 2. If IDB returns nothing (old messages before batchId was stored, or
   *    batchId is null) → fall back to the previous in-memory scan of
   *    allMessages, then ultimately to just the primary msgId.
   *
   * Returns: Array<{ chatKey: string, msgId: string }>
   */
  const findBatchSiblings = useCallback(async (primaryMsgId, batchId) => {
    // ── 1. IDB lookup by batchId (authoritative) ──────────────────────────
    if (batchId) {
      try {
        const idbRecords = await getScheduledMessagesByBatchId(batchId);
        if (idbRecords.length > 0) {
          return idbRecords.map((r) => ({
            chatKey: r.chatKey,
            msgId:   r.msgId,
          }));
        }
      } catch {
        // IDB lookup failed, falling back to memory
      }
    }

    // ── 2. In-memory fallback (allMessages scan) ───────────────────────────
    if (batchId && allMessages) {
      const results = [];
      for (const [chatKey, msgList] of Object.entries(allMessages)) {
        if (!Array.isArray(msgList)) continue;
        for (const m of msgList) {
          const id = m.id || m.messageId;
          if (m.batchId === batchId) {
            results.push({ chatKey, msgId: id });
          }
        }
      }
      if (results.length > 0) return results;
    }

    // ── 3. Last resort: just the primary message ───────────────────────────
    return [{ chatKey: getChatKey(), msgId: primaryMsgId }];
  }, [allMessages, selectedItem, getUserKey, getGroupKey]);

  /* =========================================================
     🗂️ SIDEBAR PREVIEW HELPERS
  ========================================================= */

  /**
   * After a scheduled message is DELETED, find the next most-recent
   * remaining scheduled (or regular) message in that chat to use as
   * the new preview, or clear it if none exist.
   */
  const _updatePreviewAfterDelete = useCallback((chatKey, deletedMsgIds) => {
    if (!setUsers || !setGroups) return;

    const isGroup = chatKey.startsWith("group-");

    const remaining = (allMessages?.[chatKey] || [])
      .filter((m) => {
        const id = m.id || m.messageId;
        return !deletedMsgIds.has(id);
      })
      .slice()
      .sort((a, b) => {
        const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
        return tb - ta;
      });

    const newest = remaining[0] ?? null;

    const lastMessage = newest
      ? newest.scheduledAt
        ? {
            text:        newest.text || "📎 Media",
            isDeleted:   false,
            isScheduled: true,
            scheduledAt: new Date(newest.scheduledAt).toISOString(),
          }
        : {
            text:        newest.text || (newest.media ? "📎 Media" : ""),
            isDeleted:   !!newest.isDeleted,
            isScheduled: false,
            scheduledAt: null,
          }
      : { text: "", isDeleted: false, isScheduled: false, scheduledAt: null };

    if (isGroup) {
      const groupId = chatKey.replace("group-", "");
      setGroups((prev) =>
        prev.map((g) =>
          String(g.groupId) === String(groupId)
            ? { ...g, lastMessage }
            : g
        )
      );
    } else {
      const chatId = chatKey.replace("user-", "");
      setUsers((prev) =>
        prev.map((u) =>
          String(u.chatId) === String(chatId)
            ? { ...u, lastMessage }
            : u
        )
      );
    }
  }, [allMessages, setUsers, setGroups]);

  /**
   * After a scheduled message is EDITED, update the sidebar preview text.
   */
  const _updatePreviewAfterEdit = useCallback((chatKey, msgId, newText, scheduledAt) => {
    if (!setUsers || !setGroups) return;

    const isGroup = chatKey.startsWith("group-");

    const messages = allMessages?.[chatKey] || [];
    const sorted = messages.slice().sort((a, b) => {
      const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return tb - ta;
    });

    const newestId = sorted[0]?.id || sorted[0]?.messageId;
    if (newestId && newestId !== msgId) return;

    const lastMessage = {
      text:        newText || "📎 Media",
      isDeleted:   false,
      isScheduled: true,
      scheduledAt: new Date(scheduledAt).toISOString(),
    };

    if (isGroup) {
      const groupId = chatKey.replace("group-", "");
      setGroups((prev) =>
        prev.map((g) =>
          String(g.groupId) === String(groupId)
            ? { ...g, lastMessage }
            : g
        )
      );
    } else {
      const chatId = chatKey.replace("user-", "");
      setUsers((prev) =>
        prev.map((u) =>
          String(u.chatId) === String(chatId)
            ? { ...u, lastMessage }
            : u
        )
      );
    }
  }, [allMessages, setUsers, setGroups]);

  /* =========================================================
     🗑️ DELETE
     Resolves siblings via IDB batchId first, then falls back.
  ========================================================= */
  const deleteSchedule = useCallback(async ({ msgId, batchId }) => {
    if (!socket || !msgId) return;

    const siblings = await findBatchSiblings(msgId, batchId);

    const deletedIds = new Set(siblings.map((s) => s.msgId));

    // ── Optimistic UI: remove messages ──────────────────────────────────────
    setMessages((prev) => {
      const next = { ...prev };
      for (const { chatKey, msgId: id } of siblings) {
        const list = next[chatKey] || [];
        next[chatKey] = list.filter((m) => (m.id || m.messageId) !== id);
      }
      return next;
    });

    // ── Sidebar preview ──────────────────────────────────────────────────────
    const affectedChatKeys = [...new Set(siblings.map((s) => s.chatKey))];
    for (const chatKey of affectedChatKeys) {
      _updatePreviewAfterDelete(chatKey, deletedIds);
    }

    // ── IDB ─────────────────────────────────────────────────────────────────
    for (const { msgId: id } of siblings) {
      deleteScheduledMessage(id).catch(() => {});
    }

    // ── Socket ──────────────────────────────────────────────────────────────
    for (const { chatKey, msgId: id } of siblings) {
      const isGroup = chatKey.startsWith("group-");
      if (isGroup) {
        socket.emit("schedule:group:delete", { msgId: id });
      } else {
        socket.emit("schedule:dm:delete", { msgId: id });
      }
    }

  }, [socket, selectedItem, setMessages, findBatchSiblings, _updatePreviewAfterDelete]);

  /* =========================================================
     ✏️ EDIT
     Resolves siblings via IDB batchId first, then falls back.
     For each sibling the IDB record is read to get the stored
     chatKey and chatType so we can re-encrypt correctly and
     write the updated sender-copy back to IDB.
  ========================================================= */
  const editSchedule = useCallback(async ({
    msgId,
    newText,
    scheduledAt,
    batchId,
    encryptedMedia,
    replyToMessageId,
    replyPreviewEncrypted,
  }) => {
    if (!socket || !msgId) return;
    if (!scheduledAt) return;

    const trimmed = newText?.trim();

    const siblings = await findBatchSiblings(msgId, batchId);

    // ── Optimistic UI: update text in every sibling ──────────────────────────
    if (trimmed) {
      setMessages((prev) => {
        const next = { ...prev };
        for (const { chatKey, msgId: id } of siblings) {
          const list = next[chatKey] || [];
          next[chatKey] = list.map((m) =>
            (m.id || m.messageId) !== id
              ? m
              : { ...m, text: trimmed, scheduledAt }
          );
        }
        return next;
      });

      for (const { chatKey, msgId: id } of siblings) {
        _updatePreviewAfterEdit(chatKey, id, trimmed, scheduledAt);
      }
    }

    // ── Process each sibling independently ──────────────────────────────────
    for (const { chatKey, msgId: id } of siblings) {
      const isGroup = chatKey.startsWith("group-");

      const basePayload = {
        msgId: id,
        scheduledAt,
        ...(encryptedMedia        !== undefined && { encryptedMedia }),
        ...(replyToMessageId      !== undefined && { replyToMessageId }),
        ...(replyPreviewEncrypted !== undefined && { replyPreviewEncrypted }),
      };

      /* ════════════════════════════════════
         GROUP EDIT
      ════════════════════════════════════ */
      if (isGroup) {
        const groupId = chatKey.replace("group-", "");
        const payload = { ...basePayload };

        if (trimmed) {
          try {
            const groupKey = await loadGroupKey(groupId);
            if (!groupKey) continue;

            const enc = await encryptGroup(trimmed, groupKey);
            if (!enc) continue;

            payload.newCiphertext = enc.ciphertext;
            payload.newIv         = enc.iv;

            // ── Update sender-copy in IDB ──────────────────────────────────
            void saveScheduledMessage({
              msgId: id,
              chatKey,
              scheduledAt,
              encryptedText: { ciphertext: enc.ciphertext, iv: enc.iv },
            }).catch(() => {});

          } catch {
            continue;
          }
        }

        socket.emit("schedule:group:edit", payload);

      /* ════════════════════════════════════
         DM EDIT
      ════════════════════════════════════ */
      } else {
        const peerId = chatKey.replace("user-", "");
        const payload = { ...basePayload };

        if (trimmed) {
          try {
            const [recipientPublicKey, senderPublicKey] = await Promise.all([
              fetchAndCachePublicKey(peerId,   socket),
              fetchAndCachePublicKey(myChatId, socket),
            ]);

            if (!recipientPublicKey || !senderPublicKey) continue;

            const result = await encryptDualDm(trimmed, recipientPublicKey, senderPublicKey);
            if (!result) continue;

            const { forRecipient, forSender } = result;

            payload.newCiphertext               = forRecipient.ciphertext;
            payload.newIv                       = forRecipient.iv;
            payload.newEphemeralPublicKey        = forRecipient.ephemeralPublicKey;

            payload.newCiphertextSender         = forSender.ciphertext;
            payload.newIvSender                 = forSender.iv;
            payload.newEphemeralPublicKeySender  = forSender.ephemeralPublicKey;

            // ── Update sender-copy in IDB ──────────────────────────────────
            void saveScheduledMessage({
              msgId: id,
              chatKey,
              senderChatId: myChatId,
              recipientChatId: peerId,
              fromMe: true,
              messageType: "text",

              encryptedText: {
                ciphertext: forSender.ciphertext,
                iv: forSender.iv,

                // ✅ preserve same stored shape as original schedule send
                ephemeralPublicKey:
                  typeof forSender.ephemeralPublicKey === "string"
                    ? forSender.ephemeralPublicKey
                    : JSON.stringify(forSender.ephemeralPublicKey),
              },

              encryptedMedia: encryptedMedia ?? null,
              replyToMessageId: replyToMessageId ?? null,
              replyPreviewEncrypted: replyPreviewEncrypted ?? null,
              scheduledAt,
              timestamp: Date.now(),
              _scheduled: true,
            }).catch(() => {});

          } catch {
            continue;
          }
        }

        socket.emit("schedule:dm:edit", payload);
      }
    }

  }, [socket, myChatId, selectedItem, setMessages, findBatchSiblings, _updatePreviewAfterEdit]);

  return {
    deleteSchedule,
    editSchedule,
  };
}